import PageHero from "@/components/PageHero";
import EditorialMarquee from "@/components/EditorialMarquee";
import EnvironmentalIndicators from "@/components/EnvironmentalIndicators";
import ClimateTimeline from "@/components/ClimateTimeline";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import ClimateStories from "@/components/ClimateStories";
import TranslatedProse from "@/components/TranslatedProse";
import { motion } from "framer-motion";

const HERO_ART = "https://customer-assets-m6fa6gv7.emergentagent.net/job_green-timeline/artifacts/kvkjgxq7_climate-change-illustration.jpg";

const IMG = {
  glacier: "https://images.pexels.com/photos/30412602/pexels-photo-30412602.jpeg?auto=compress&cs=tinysrgb&w=1800",
  forest: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1800&auto=format&fit=crop",
  storm: "https://images.pexels.com/photos/4028958/pexels-photo-4028958.jpeg?auto=compress&cs=tinysrgb&w=1800",
  reef: "https://images.unsplash.com/photo-1682687982501-1e58ab814714?w=1800&auto=format&fit=crop",
  wildfire: "https://images.pexels.com/photos/7913319/pexels-photo-7913319.jpeg?auto=compress&cs=tinysrgb&w=1800",
  desert: "https://images.pexels.com/photos/36767372/pexels-photo-36767372.jpeg?auto=compress&cs=tinysrgb&w=1800",
  bird: "https://images.unsplash.com/photo-1591824438708-ce405f36ba3d?w=1800&auto=format&fit=crop",
  river: "https://images.unsplash.com/photo-1500581276021-a4bbcd0050c5?w=1800&auto=format&fit=crop",
  mountains: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1800&auto=format&fit=crop",
  farmer: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1800&auto=format&fit=crop",
  turbines: "https://images.pexels.com/photos/30037318/pexels-photo-30037318.jpeg?auto=compress&cs=tinysrgb&w=1800",
  aurora: "https://images.pexels.com/photos/31585091/pexels-photo-31585091.jpeg?auto=compress&cs=tinysrgb&w=1800",
};

function Chapter({ eyebrow, title, id, children }) {
  return (
    <section id={id} className="max-w-4xl mx-auto px-6 md:px-10 py-16 md:py-24">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-15%" }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-muted)] mb-4">{eyebrow}</div>
        <h2 className="font-serif-display text-5xl md:text-7xl leading-[0.95] tracking-tighter mb-8">{title}</h2>
        <div className="space-y-6 text-lg leading-relaxed text-[color:var(--np-text-2)]">{children}</div>
      </motion.div>
    </section>
  );
}

function ImagePlate({ src, alt, caption, aspect = "aspect-[16/9]" }) {
  return (
    <motion.figure
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10%" }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="max-w-6xl mx-auto px-6 md:px-10 my-16 md:my-24"
      data-testid={`cc-image-${alt.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div className={`relative w-full ${aspect} overflow-hidden bg-[color:var(--np-bg-2)] shadow-[0_40px_100px_-40px_rgba(228,87,46,0.35)]`}>
        <img src={src} alt={alt} className="w-full h-full object-cover" loading="lazy" />
      </div>
      {caption && (
        <figcaption className="mt-3 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-muted)]">
          {caption}
        </figcaption>
      )}
    </motion.figure>
  );
}

const PILLARS = [
  { verb: "Observe", body: "Every moment of change." },
  { verb: "Record",  body: "Every detail that matters." },
  { verb: "Understand", body: "Patterns, trends, and impacts." },
  { verb: "Preserve", body: "Knowledge for future generations." },
];

const DAILY = [
  "A glacier slowly retreats.",
  "A river rises after heavy rainfall.",
  "A coral reef experiences warmer waters.",
  "Migratory birds arrive earlier than expected.",
  "A forest recovers after a wildfire.",
  "A new weather record is set.",
  "A community experiences its hottest summer.",
];

const ALMANAC = [
  "Daily weather observations","Historic storms","Hurricanes, cyclones & typhoons","Tornadoes","Floods","Droughts","Heatwaves","Cold waves","Snowstorms","Wildfires","Dust storms","Hail events","Lightning outbreaks","Fog events","Record temperatures","Rainfall records","Snowfall records","Atmospheric phenomena","Seasonal climate patterns",
];

const TRACKING = [
  "Forests","Rivers","Lakes","Wetlands","Coastlines","Mountains","Glaciers","Coral reefs","Wildlife habitats","Bird migrations","Plant flowering seasons","Agriculture","Air quality","Water quality","Biodiversity","Ecosystem health",
];

const AUDIENCES = ["Families","Students","Teachers","Researchers","Journalists","Environmental orgs","Conservation groups","Emergency planners","Historians","Photographers","Nature enthusiasts"];

const PREPARE = ["Understand recurring weather patterns","Learn from past disasters","Improve emergency planning","Protect vulnerable ecosystems","Support conservation efforts","Plan sustainable development","Build climate resilience","Preserve natural heritage"];

const CONTRIBUTIONS = ["Wildlife sightings","Nature photography","Weather observations","Historic weather memories","Seasonal changes","Flower blooms","Bird migrations","Scenic landscapes","Environmental stories","Conservation projects"];

export default function ClimateChange() {
  return (
    <TranslatedProse cacheKey="climate-change">
      <PageHero
        eyebrow="/ Climate Change · Our Planet · Our Future · Our Responsibility"
        title="Understanding our changing planet."
        subtitle="The Earth is constantly changing. By observing, recording, and learning from those changes, we can better understand our world and prepare for the future."
        image={IMG.storm}
      />

      <EditorialMarquee terms={["OBSERVE", "RECORD", "UNDERSTAND", "PRESERVE", "EVERY DAY · EVERY PLACE", "A SOCIAL PULSE ENDEAVOR"]} />

      {/* Featured illustration */}
      <motion.figure
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-10%" }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-6xl mx-auto px-6 md:px-10 pt-24 md:pt-32"
        data-testid="cc-hero-illustration"
      >
        <div className="relative w-full overflow-hidden bg-[color:var(--np-bg-2)] shadow-[0_60px_140px_-40px_rgba(228,87,46,0.45)]">
          <img
            src={HERO_ART}
            alt="Climate Change — Our Planet. Our Future. Our Responsibility."
            className="w-full h-auto"
          />
        </div>
        <figcaption className="mt-4 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-muted)]">
          What we document · The impacts we face · You can make a difference
        </figcaption>
      </motion.figure>

      <Chapter eyebrow="/01 · Welcome" title="Welcome to Climate Change.">
        <p>
          Climate has always influenced the way people live. It determines where forests grow, where rivers flow, where crops thrive, when birds migrate, and how communities prepare for the changing seasons. Throughout history, societies have adapted to floods, droughts, storms, and changing weather patterns. Today, advances in science and technology allow us to observe these changes with greater detail than ever before.
        </p>
        <p>
          Climate Change is one of the most important subjects of our time because it affects every continent, every country, every ecosystem, and every community.
        </p>
        <p>
          The Climate Change section of NaturePulse, part of the SocialPulse ecosystem, is dedicated to documenting the Earth's changing environment through accurate observations, historical records, scientific information, and community contributions.
        </p>

        {/* Mission pillars */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-black/10 mt-8">
          {PILLARS.map(p => (
            <div key={p.verb} className="bg-[color:var(--np-bg)] p-6">
              <div className="font-mono-tech text-[10px] tracking-[0.35em] uppercase text-[color:var(--np-brand)] mb-2">Mission</div>
              <div className="font-serif-display text-3xl leading-none mb-2">{p.verb}.</div>
              <div className="text-sm text-[color:var(--np-text-2)]">{p.body}</div>
            </div>
          ))}
        </div>
      </Chapter>

      <ImagePlate src={IMG.glacier} alt="Glacier under stormy skies" caption="Glaciers · a silent, measurable record of a warming planet" />

      <Chapter eyebrow="/02 · A living record" title="A living record of our planet.">
        <p>Every day, millions of changes occur across Earth.</p>
        <ul className="space-y-2 text-[color:var(--np-text)] font-serif-display text-xl">
          {DAILY.map(d => (
            <li key={d} className="flex items-baseline gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--np-brand)] inline-block" />{d}
            </li>
          ))}
        </ul>
        <p>Each event is one piece of a much larger story. NaturePulse is building a permanent environmental archive where these observations become part of a global timeline that can be explored by everyone.</p>
      </Chapter>

      <Chapter eyebrow="/03 · Why daily documentation matters" title="One hot day is not a trend.">
        <p>Understanding long-term environmental change requires consistent observations over many years. One unusually hot day does not define a climate trend. One severe storm does not explain long-term weather patterns. Meaningful understanding comes from carefully documenting thousands of observations across many seasons, years, and decades.</p>
        <p>NaturePulse is designed to become one of the world's largest continuously growing collections of environmental observations by documenting nature and weather every single day.</p>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-6 text-[color:var(--np-text)] font-serif-display text-xl">
          {["Every sunrise photographed.","Every rainfall measured.","Every bird migration recorded.","Every flowering season observed.","Every storm documented.","Every weather record preserved."].map(x => (
            <li key={x} className="flex items-baseline gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--np-live)] inline-block" />{x}
            </li>
          ))}
        </ul>
      </Chapter>

      <EnvironmentalIndicators />

      <ImagePlate src={IMG.reef} alt="Coral reef under warming ocean" caption="Coral reefs · the fourth global bleaching event underway" />

      <Chapter eyebrow="/04 · Nature and weather are connected" title="You can't separate what you observe.">
        <p>Nature and weather cannot be separated. Weather influences every living ecosystem.</p>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-6 text-[color:var(--np-text)] font-serif-display text-lg">
          {["Temperature affects flowering plants.","Rainfall shapes rivers and wetlands.","Snowfall supplies mountain watersheds.","Ocean temperatures influence marine life.","Wind patterns affect migration.","Seasonal cycles determine agriculture.","Wildlife responds to changing habitats."].map(x => (
            <li key={x} className="flex items-baseline gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[color:var(--np-brand)] inline-block" />{x}</li>
          ))}
        </ul>
        <p>NaturePulse brings these connections together in one place, allowing people to see how landscapes, biodiversity, and weather interact over time.</p>
      </Chapter>

      <ImagePlate src={IMG.storm} alt="Storm systems intensifying" caption="Storm systems · rapid intensification more common" />

      <Chapter eyebrow="/05 · Weather Almanac" title="A record of every storm.">
        <p>At the heart of NaturePulse is the <span className="text-[color:var(--np-brand)] font-medium">Global Weather Almanac</span>. This growing archive documents weather events from every corner of the world.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-black/10 mt-4">
          {ALMANAC.map(item => (
            <div key={item} className="bg-[color:var(--np-bg)] p-4 font-serif-display text-lg">{item}</div>
          ))}
        </div>
        <p>Each event includes location, date, geographic information, photographs, videos, news articles, scientific context, and community experiences — a lasting historical resource.</p>
      </Chapter>

      <ClimateTimeline />

      <ImagePlate src={IMG.river} alt="River shaping the land" caption="Rivers · shaping ecosystems and human civilisation" />

      <Chapter eyebrow="/06 · Tracking environmental change" title="Not just weather. The whole ecosystem.">
        <p>NaturePulse documents much more than weather. The platform also records long-term changes in:</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-black/10">
          {TRACKING.map(item => (
            <div key={item} className="bg-[color:var(--np-bg)] p-4 font-serif-display text-lg">{item}</div>
          ))}
        </div>
        <p>By connecting these observations with weather and climate records, NaturePulse provides a broader understanding of environmental change.</p>
      </Chapter>

      <BeforeAfterSlider />

      <ImagePlate src={IMG.wildfire} alt="Wildfire behaviour changing" caption="Wildfire seasons · longer, hotter, and more frequent" />

      <Chapter eyebrow="/07 · Science meets community" title="Science + citizens.">
        <p>Some of the world's most valuable environmental records come from ordinary people paying attention to the world around them. NaturePulse combines:</p>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-6 text-[color:var(--np-text)] font-serif-display text-lg">
          {["Scientific research","Meteorological observations","Satellite imagery","Government datasets","Historical records","Academic publications","Community photography","Wildlife sightings","Local weather observations","Citizen science contributions"].map(x => (
            <li key={x} className="flex items-baseline gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[color:var(--np-live)] inline-block" />{x}</li>
          ))}
        </ul>
        <p>Together, these sources create a richer picture than any one source could provide alone.</p>
      </Chapter>

      <ImagePlate src={IMG.farmer} alt="Farmer working with changing seasons" caption="Communities · every observer, a data point" />

      <Chapter eyebrow="/08 · Every community has a story" title="Different views. One planet.">
        <p>Climate change is experienced differently around the world.</p>
        <ul className="space-y-2 text-[color:var(--np-text)] font-serif-display text-lg">
          {["Mountain communities notice changing snow seasons.","Coastal communities observe shoreline changes.","Farmers monitor rainfall and growing seasons.","Birdwatchers notice shifts in migration.","Divers observe coral reefs.","Hikers document changing glaciers.","Urban residents experience heat islands."].map(x => (
            <li key={x} className="flex items-baseline gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[color:var(--np-brand)] inline-block" />{x}</li>
          ))}
        </ul>
        <p>By preserving these local observations, NaturePulse builds a global story from millions of individual experiences.</p>
      </Chapter>

      <Chapter eyebrow="/09 · Learning from history" title="History is our teacher.">
        <p>NaturePulse preserves environmental records so future generations can compare:</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-black/10">
          {["Then and now","Seasonal patterns","Historic photographs","Satellite imagery","River levels","Forest cover","Wildlife populations","Weather records","Temperature trends","Rainfall history","Extreme weather events"].map(x => (
            <div key={x} className="bg-[color:var(--np-bg)] p-4 font-serif-display text-lg">{x}</div>
          ))}
        </div>
        <p>These comparisons help communities better understand how their local environment has evolved over time.</p>
      </Chapter>

      <ImagePlate src={IMG.bird} alt="Bird migration shifting" caption="Migration · phenology advancing +2.5 days per decade" />

      <Chapter eyebrow="/10 · Research & education" title="An open knowledge platform.">
        <p>NaturePulse is designed as an open knowledge platform for:</p>
        <ul className="grid grid-cols-2 md:grid-cols-3 gap-3 text-[color:var(--np-text)] font-serif-display text-lg">
          {AUDIENCES.map(a => (
            <li key={a} className="flex items-baseline gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[color:var(--np-brand)] inline-block" />{a}</li>
          ))}
        </ul>
        <p>By bringing environmental information from around the world together, the platform supports education, encourages curiosity, and provides a valuable resource for exploring how weather, climate, and nature interact.</p>
      </Chapter>

      <Chapter eyebrow="/11 · Preparing for tomorrow" title="Knowledge is preparedness.">
        <p>One of the most important reasons to preserve environmental history is to improve future preparedness. Historical records help communities:</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-black/10">
          {PREPARE.map(x => (
            <div key={x} className="bg-[color:var(--np-bg)] p-4 font-serif-display text-lg">{x}</div>
          ))}
        </div>
        <p>Knowledge gained from the past can help inform decisions for the future.</p>
      </Chapter>

      <ImagePlate src={IMG.turbines} alt="Renewables at sunset" caption="Renewables · 30.3% of global electricity in 2024" />

      <Chapter eyebrow="/12 · A global community" title="Nature belongs to everyone.">
        <p>NaturePulse welcomes contributions from people everywhere. Share:</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-black/10">
          {CONTRIBUTIONS.map(x => (
            <div key={x} className="bg-[color:var(--np-bg)] p-4 font-serif-display text-lg">{x}</div>
          ))}
        </div>
        <p>Every contribution becomes part of a growing environmental archive that future generations can explore and learn from.</p>
      </Chapter>

      <ClimateStories />

      <ImagePlate src={IMG.mountains} alt="Mountain range at dawn" caption="Landscapes · an ongoing, unfinished story" />

      <Chapter eyebrow="/13 · A legacy" title="For generations to come.">
        <p>The landscapes we enjoy today are part of an ongoing story.</p>
        <ul className="space-y-2 text-[color:var(--np-text)] font-serif-display text-xl">
          {["The forests we walk through.","The rivers that sustain communities.","The mountains that shape our horizons.","The oceans that connect continents.","The changing seasons that define life across the planet."].map(x => (
            <li key={x} className="flex items-baseline gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[color:var(--np-brand)] inline-block" />{x}</li>
          ))}
        </ul>
        <p>These natural treasures deserve to be observed, understood, and preserved.</p>
        <p>NaturePulse is creating more than a collection of weather reports or nature photographs. It is building a living environmental memory of Earth — a continuously growing record that helps people appreciate the beauty of the natural world, understand long-term environmental change, and preserve knowledge for generations to come.</p>
      </Chapter>

      <ImagePlate src={IMG.aurora} alt="Aurora over an arctic landscape" caption="Solar cycle 25 · auroras intensifying" />

      {/* Signature */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 pt-8 pb-32 md:pb-40 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
        >
          <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-muted)] mb-6">/ Signature</div>
          <h2 className="font-serif-display text-6xl md:text-8xl leading-[0.9] tracking-tighter mb-6">
            <em className="text-[color:var(--np-brand)]">NaturePulse.</em>
          </h2>
          <p className="font-serif-display text-2xl md:text-3xl italic text-[color:var(--np-text)] max-w-3xl mx-auto leading-tight mb-4">
            Observe Today. Understand Tomorrow. Preserve Earth's Living History.
          </p>
          <p className="text-[color:var(--np-text-2)] max-w-2xl mx-auto text-lg">
            Every observation matters. Every location has a story. Every season adds another chapter. Every generation has the opportunity to leave behind a richer understanding of the remarkable planet we all share.
          </p>
          <div className="mt-8 font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-muted)]">
            KNOWLEDGE TODAY · ACTION TOMORROW · A BETTER WORLD FOR ALL
          </div>
        </motion.div>
      </section>
    </TranslatedProse>
  );
}
